import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    FormFeedback,
    Input,
    Row,
} from "reactstrap";
import * as Yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import Select from "react-select";

import CommonTable from "components/Common/TableContainer";
import Loaders from "components/Common/Loaders";
import { AsyncClientSelect } from "components/Common/AsyncSearchSelects";
import {
    applyClientAsyncFilterOptionToFormState,
    buildClientAsyncFilterInitialOption,
    getClientAsyncFilterLabelFieldKey,
    normalizeMetadataFilterDateForApi,
} from "helpers/metadataFilterHelpers";
import { phoneNumberShowFormat } from "helpers/GeneralHelper";
import {
    fetchReportMailSourceOptions,
    mapReportMailSourceOptions,
} from "model/InProgressReportModel";
import {
    submitCandidateSummaryReportFilterData,
    submitCandidateSummaryReportSendData,
} from "model/CandidateSummaryReportModel";

const selectMenuStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
};

const TABLE_COLUMNS = [
    { Header: "Client", accessor: "client" },
    { Header: "Name", accessor: "name" },
    { Header: "Phone", accessor: "phone" },
    { Header: "Email", accessor: "email" },
    { Header: "Position", accessor: "position" },
    { Header: "Fedex ID", accessor: "fedex_id" },
    { Header: "Update Sent", accessor: "updateSent" },
    { Header: "Notes", accessor: "notes" },
];

const strOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
};

const candidateSummaryReportFilterSchema = Yup.object().shape({
    fromDate: Yup.string().trim().required("Start date is required"),
    toDate: Yup.string().trim().required("End date is required"),
});

const FilterFieldFeedback = ({ message = "" }) => (
    <div className="filter-field-feedback-slot" aria-live="polite">
        {message ? (
            <FormFeedback type="invalid" className="d-block filter-field-feedback mb-0">
                {message}
            </FormFeedback>
        ) : null}
    </div>
);

FilterFieldFeedback.propTypes = {
    message: PropTypes.string,
};

const formatDateValue = (value) => {
    if (!value) return "";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("MM/DD/YYYY") : String(value);
};

const extractFilterResponse = (response = {}) => {
    const responseData = response?.data ?? response ?? {};
    const nestedData =
        responseData?.data && typeof responseData.data === "object"
            ? responseData.data
            : responseData;

    const rows = Array.isArray(nestedData?.rows)
        ? nestedData.rows
        : Array.isArray(responseData?.rows)
            ? responseData.rows
            : [];

    return {
        rows,
        clientId: nestedData?.clientId ?? responseData?.clientId ?? "",
        mailSourceId: nestedData?.mailSourceId ?? responseData?.mailSourceId ?? "",
    };
};

const mapRowsFromApi = (rows = []) =>
    (Array.isArray(rows) ? rows : []).map((item = {}, index) => {
        const specialNotes = String(item?.specialNotes ?? item?.specialNote ?? "").trim();

        return {
            ...item,
            id: item?.candidateClientId ?? item?.id ?? item?._id ?? `row-${index}`,
            candidateClientId: item?.candidateClientId ?? "",
            serialNumber: item?.serialNumber ?? index + 1,
            client: item?.clientName ?? item?.client ?? "",
            name: item?.candidateFullName ?? item?.name ?? "",
            specialNotes,
            hasSpecialNote: Boolean(
                item?.hasSpecialNote ?? item?.isSpecialNote ?? specialNotes
            ),
            phone: phoneNumberShowFormat(item?.candidatePhone ?? item?.phone ?? ""),
            email: item?.candidateEmail ?? item?.email ?? "",
            position: item?.jobName ?? item?.position ?? "",
            fedex_id: item?.fedexId ?? item?.fedex_id ?? "",
            isExistingFedexDriver: Boolean(item?.isExistingFedexDriver),
            updateSent:
                item?.updateSentDateRelative ??
                formatDateValue(item?.updateSentDate) ??
                "",
            notes: item?.notes ?? "",
        };
    });

const CandidateSummaryReport = () => {
    const [filterForm, setFilterForm] = useState({
        fromDate: "",
        toDate: "",
        clientId: "",
        [getClientAsyncFilterLabelFieldKey()]: "",
    });
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [mailSourceOptions, setMailSourceOptions] = useState([]);
    const [emailFrom, setEmailFrom] = useState(null);
    const [preferredMailSourceId, setPreferredMailSourceId] = useState("");
    const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
    const [sendingMail, setSendingMail] = useState(false);
    const [filterErrors, setFilterErrors] = useState({});

    const resolveRowCandidateClientId = useCallback(
        (row = {}) => String(row?.candidateClientId ?? row?.id ?? "").trim(),
        []
    );

    useEffect(() => {
        const loadMailSourceOptions = async () => {
            const response = await fetchReportMailSourceOptions();
            if (response == null || response?.success === false) {
                setMailSourceOptions([]);
                return;
            }
            setMailSourceOptions(mapReportMailSourceOptions(response));
        };
        loadMailSourceOptions();
    }, []);

    useEffect(() => {
        if (!mailSourceOptions.length) {
            setEmailFrom(null);
            return;
        }
        const preferredId = strOrNull(preferredMailSourceId);
        const matchedOption = preferredId
            ? mailSourceOptions.find((option) => String(option.value) === preferredId)
            : null;
        setEmailFrom(matchedOption ?? mailSourceOptions[0]);
    }, [mailSourceOptions, preferredMailSourceId]);

    const filteredData = useMemo(() => reportData, [reportData]);

    const buildFilterPayload = () => ({
        clientId: strOrNull(filterForm?.clientId),
        dateFrom: normalizeMetadataFilterDateForApi(filterForm?.fromDate),
        dateTo: normalizeMetadataFilterDateForApi(filterForm?.toDate),
    });

    const getCandidateSummaryReportList = async () => {
        try {
            await candidateSummaryReportFilterSchema.validate(filterForm, {
                abortEarly: false,
            });
            setFilterErrors({});
        } catch (validationError) {
            const nextErrors = {};
            validationError?.inner?.forEach((error) => {
                nextErrors[error.path] = error.message;
            });
            setFilterErrors(nextErrors);
            return;
        }

        setLoading(true);
        setSelectedCandidateIds([]);
        try {
            const response = await submitCandidateSummaryReportFilterData(
                buildFilterPayload()
            );
            const { rows, mailSourceId: responseMailSourceId } =
                extractFilterResponse(response);

            if (response == null || response?.success === false) {
                setReportData([]);
                setPreferredMailSourceId("");
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }

            setPreferredMailSourceId(strOrNull(responseMailSourceId) || "");
            setReportData(mapRowsFromApi(rows));
        } catch (error) {
            toast.error(error?.message || "Unable to load candidate summary report");
            setReportData([]);
            setPreferredMailSourceId("");
            setSelectedCandidateIds([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectionChange = useCallback((selectedIds = []) => {
        setSelectedCandidateIds(
            Array.isArray(selectedIds)
                ? selectedIds.map((id) => String(id).trim()).filter(Boolean)
                : []
        );
    }, []);

    const handleNotesBlur = useCallback(
        (row = {}, notesValue = "") => {
            const rowId = resolveRowCandidateClientId(row);
            if (!rowId) return;
            setReportData((previousRows) =>
                previousRows.map((item) =>
                    resolveRowCandidateClientId(item) === rowId
                        ? { ...item, notes: String(notesValue ?? "") }
                        : item
                )
            );
        },
        [resolveRowCandidateClientId]
    );

    const buildSendMailPayload = () => {
        const selectedIdSet = new Set(selectedCandidateIds);
        return {
            clientId: strOrNull(filterForm?.clientId),
            mailSourceId: strOrNull(emailFrom?.value),
            dateFrom: normalizeMetadataFilterDateForApi(filterForm?.fromDate),
            dateTo: normalizeMetadataFilterDateForApi(filterForm?.toDate),
            items: reportData.map((row) => {
                const candidateClientId = resolveRowCandidateClientId(row);
                return {
                    candidateClientId,
                    isSelected: selectedIdSet.has(candidateClientId),
                    notes: String(row?.notes ?? ""),
                };
            }),
        };
    };

    const handleSendMail = async () => {
        const clientId = strOrNull(filterForm?.clientId);
        const mailSourceId = strOrNull(emailFrom?.value);

        // if (!clientId) {
        //     toast.error("Please select a client");
        //     return;
        // }
        if (!mailSourceId) {
            toast.error("Mail source is required");
            return;
        }
        if (!reportData.length) {
            toast.error("No candidates available to send");
            return;
        }
        if (!selectedCandidateIds.length) {
            toast.error("Please select at least one candidate");
            return;
        }

        setSendingMail(true);
        try {
            const response = await submitCandidateSummaryReportSendData(
                buildSendMailPayload()
            );

            if (response == null || response?.success === false) {
                toast.error(response?.message || "Unable to send mail");
                return;
            }

            toast.success(response?.message || "Mail sent successfully");
            setSelectedCandidateIds([]);
            await getCandidateSummaryReportList();
        } catch (error) {
            toast.error(error?.message || "Unable to send mail");
        } finally {
            setSendingMail(false);
        }
    };

    const handleFilterFieldChange = (event) => {
        const { name, value } = event.target;
        setFilterForm((previous) => ({
            ...previous,
            [name]: value,
        }));
        if (filterErrors?.[name]) {
            setFilterErrors((previous) => ({
                ...previous,
                [name]: "",
            }));
        }
    };

    return (
        <React.Fragment>
            <div className="page-content mail-report-page">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Candidate Summary Report</h5>
                            <hr />

                            <div className="upper-filter-bar manage-user-filter-strip">
                                <Row className="g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">Start Date</span>
                                        <Input
                                            type="date"
                                            name="fromDate"
                                            className={`border rounded w-100 category-search-input${
                                                filterErrors?.fromDate ? " is-invalid" : ""
                                            }`}
                                            value={filterForm?.fromDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                        <FilterFieldFeedback message={filterErrors?.fromDate} />
                                    </Col>
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">End Date</span>
                                        <Input
                                            type="date"
                                            name="toDate"
                                            className={`border rounded w-100 category-search-input${
                                                filterErrors?.toDate ? " is-invalid" : ""
                                            }`}
                                            value={filterForm?.toDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                        <FilterFieldFeedback message={filterErrors?.toDate} />
                                    </Col>
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">Client</span>
                                        <AsyncClientSelect
                                            key={`candidate-summary-client-${filterForm?.clientId || ""}`}
                                            pageName="ReportFiltered"
                                            value={filterForm?.clientId || ""}
                                            initialOption={buildClientAsyncFilterInitialOption(
                                                filterForm?.clientId,
                                                filterForm[getClientAsyncFilterLabelFieldKey()]
                                            )}
                                            onChange={(option) =>
                                                setFilterForm((previous) =>
                                                    applyClientAsyncFilterOptionToFormState(
                                                        previous,
                                                        option
                                                    )
                                                )
                                            }
                                            placeholder="Select Client"
                                            className="select2-selection w-100"
                                        />
                                        <FilterFieldFeedback />
                                    </Col>
                                    <Col xs="12" md="6" lg={2}>
                                        <span
                                            className="manage-user-filter-label"
                                            aria-hidden="true"
                                        >
                                            &nbsp;
                                        </span>
                                        <Button
                                            color="primary"
                                            className="w-100 manage-user-search-btn"
                                            type="button"
                                            disabled={loading}
                                            onClick={getCandidateSummaryReportList}
                                        >
                                            <i className="fa fa-search me-1" aria-hidden="true" />
                                            Search
                                        </Button>
                                        <FilterFieldFeedback />
                                    </Col>
                                </Row>
                            </div>

                            <div className="upper-filter-bar manage-user-filter-strip mail-report-toolbar">
                                <Row className="g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="6" lg={6}>
                                        <span className="manage-user-filter-label">Mail</span>
                                        <Select
                                            className="select2-selection"
                                            options={mailSourceOptions}
                                            value={emailFrom}
                                            onChange={setEmailFrom}
                                            placeholder="Select mail source"
                                            isSearchable={false}
                                            isDisabled={!mailSourceOptions.length}
                                            menuPortalTarget={
                                                typeof document !== "undefined"
                                                    ? document.body
                                                    : null
                                            }
                                            menuPosition="fixed"
                                            styles={selectMenuStyles}
                                        />
                                    </Col>
                                </Row>
                            </div>

                            <Row className="align-items-center mb-3">
                                <Col className="text-end">
                                    <Button
                                        color="primary"
                                        type="button"
                                        disabled={sendingMail || loading || !reportData.length}
                                        onClick={handleSendMail}
                                    >
                                        <i
                                            className="mdi mdi-email-outline me-1"
                                            aria-hidden="true"
                                        />
                                        {sendingMail ? "Sending…" : "Send Mail"}
                                    </Button>
                                </Col>
                            </Row>

                            {loading ? (
                                <Loaders />
                            ) : (
                                <CommonTable
                                    columns={TABLE_COLUMNS}
                                    data={filteredData}
                                    isActionRequired={false}
                                    enableRowSelection
                                    isCheckboxLeftSide
                                    pageName="CandidateSummaryReport"
                                    resolveRowId={resolveRowCandidateClientId}
                                    onSelectionChange={handleSelectionChange}
                                    onNotesBlur={handleNotesBlur}
                                />
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

CandidateSummaryReport.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(CandidateSummaryReport);
